<div class="card">
    <div class="font-semibold text-xl mb-4">Websites</div>

    <p-table
        [value]="filteredRealms()"
        [loading]="realmService.loading()"
        dataKey="RealmId"
        [rowTrackBy]="trackByRealmId"
        [rows]="10"
        [paginator]="true"
        [showGridlines]="true"
        [sortField]="sortField()"
        [sortOrder]="sortOrder()"
        (onSort)="onSortChange($event)"
        responsiveLayout="scroll"
    >
        <ng-template #caption>
            <div class="flex items-center justify-between gap-3 flex-wrap">
                <div class="flex items-center gap-2 flex-wrap">
                    <p-iconfield>
                        <p-inputicon styleClass="pi pi-search" />
                        <input
                            pInputText
                            type="text"
                            placeholder="Search by name or domain…"
                            [ngModel]="searchTerm()"
                            (ngModelChange)="searchTerm.set($event ?? '')"
                        />
                    </p-iconfield>
                    @if (canManage()) {
                    <p-multiselect
                        [options]="companyFilterOptions()"
                        [ngModel]="selectedCompanyIds()"
                        (ngModelChange)="selectedCompanyIds.set($event ?? [])"
                        placeholder="Company"
                        [filter]="true"
                        [showClear]="true"
                        display="chip"
                        appendTo="body"
                        styleClass="w-full md:w-64"
                    ></p-multiselect>
                    } @if (hasActiveFilters()) {
                    <p-button
                        label="Clear"
                        icon="pi pi-filter-slash"
                        severity="secondary"
                        [text]="true"
                        size="small"
                        (onClick)="clearFilters()"
                    />
                    }
                </div>
                <div class="flex items-center gap-2">
                    <p-button
                        label="Export CSV"
                        icon="pi pi-download"
                        severity="secondary"
                        outlined
                        [disabled]="filteredRealms().length === 0"
                        (onClick)="exportCsv()"
                    />
                    <p-button
                        label="Refresh"
                        icon="pi pi-refresh"
                        severity="secondary"
                        outlined
                        [loading]="realmService.loading()"
                        (onClick)="refresh()"
                    />
                    @if (canManage()) {
                    <p-button
                        label="Add Website"
                        icon="pi pi-plus"
                        (onClick)="openAdd()"
                    />
                    }
                </div>
            </div>
        </ng-template>
        <ng-template #header>
            <tr>
                <th pSortableColumn="CreatedAt">
                    Created at <p-sortIcon field="CreatedAt" />
                </th>
                <th pSortableColumn="DisplayName">
                    Name <p-sortIcon field="DisplayName" />
                </th>
                <th pSortableColumn="Domain">
                    Domain <p-sortIcon field="Domain" />
                </th>
                <th pSortableColumn="Category.Name">
                    Category <p-sortIcon field="Category.Name" />
                </th>
                <th>Site Template</th>
                <th>Currencies</th>
                <th>Company</th>
                <th>Country</th>
                <th>Languages</th>
                <th style="width: 11rem">Game Server</th>
                <th style="width: 8rem">Actions</th>
            </tr>
        </ng-template>
        <ng-template #body let-realm>
            <tr>
                <td>
                    <span class="text-sm text-muted-color"
                        >{{ realm.CreatedAt | date:'medium' }}</span
                    >
                </td>
                <td>
                    @if (realm.DisplayName) {
                    <span class="font-medium">{{ realm.DisplayName }}</span>
                    } @else {
                    <span
                        class="text-xs text-muted-color italic"
                        pTooltip="No display name set — fill in Branding on the website detail"
                        tooltipPosition="top"
                        >—</span
                    >
                    }
                </td>
                <td>
                    <a
                        class="text-primary hover:underline inline-flex items-center gap-1 break-all"
                        [href]="'https://' + realm.Domain"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {{ realm.Domain }}
                        <i
                            class="pi pi-external-link"
                            style="font-size: 0.7rem"
                        ></i>
                    </a>
                </td>
                <td>
                    @if (realm.Category?.Name) {
                    <span
                        pTooltip="{{ realm.Category.Type }}"
                        tooltipPosition="top"
                        >{{ realm.Category.Name }}</span
                    >
                    } @else {
                    <span class="text-xs text-muted-color italic">—</span>
                    }
                </td>
                <td>
                    @if (realm.SiteTemplate; as tpl) {
                    <div class="flex items-center gap-2">
                        <span
                            class="truncate"
                            pTooltip="{{ tpl.Id }}"
                            tooltipPosition="top"
                            >{{ tpl.Name }}</span
                        >
                        @if (tpl.CurrentVersion) {
                        <p-tag
                            severity="success"
                            [value]="'v' + tpl.CurrentVersion"
                        />
                        } @else {
                        <p-tag
                            severity="danger"
                            value="offline"
                            pTooltip="Template has no published version — site serves 404"
                            tooltipPosition="top"
                        />
                        }
                    </div>
                    } @else {
                    <span
                        class="text-xs text-muted-color italic"
                        pTooltip="No template assigned — site offline"
                        tooltipPosition="top"
                        >—</span
                    >
                    }
                </td>
                <td>
                    <span
                        class="text-xs text-muted-color italic"
                        pTooltip="Not Implemented — backend field pending"
                        tooltipPosition="top"
                        >—</span
                    >
                </td>
                <td>
                    @if (companyForRealm(realm); as c) {
                    <span
                        class="truncate"
                        pTooltip="{{ c.name }}"
                        tooltipPosition="top"
                        >{{ c.name }}</span
                    >
                    } @else {
                    <span
                        class="text-xs text-muted-color italic"
                        pTooltip="Company not loaded — global-admin only"
                        tooltipPosition="top"
                        >—</span
                    >
                    }
                </td>
                <td>
                    @if (companyForRealm(realm); as c) {
                    <div class="flex items-center gap-2">
                        <span
                            [class]="'flag flag-' + c.country.toLowerCase()"
                            style="width: 18px; height: 12px"
                        ></span>
                        <span>{{ countryService.getName(c.country) }}</span>
                        <span class="text-xs text-muted-color font-mono"
                            >({{ c.country }})</span
                        >
                    </div>
                    } @else {
                    <span class="text-xs text-muted-color italic">—</span>
                    }
                </td>
                <td>
                    <span
                        class="text-xs text-muted-color italic"
                        pTooltip="Not Implemented — backend field pending"
                        tooltipPosition="top"
                        >—</span
                    >
                </td>
                <td>
                    @if (providerStatusView(realm.ProviderStatus); as v) {
                    <p-tag
                        [value]="v.label"
                        [severity]="v.severity"
                        [pTooltip]="v.description"
                        tooltipPosition="top"
                    />
                    }
                </td>
                <td>
                    <div class="flex gap-1">
                        <p-button
                            icon="pi pi-pencil"
                            severity="warn"
                            [rounded]="true"
                            [outlined]="true"
                            pTooltip="Edit"
                            (onClick)="openEdit(realm.RealmId)"
                        />
                        <p-button
                            icon="pi pi-trash"
                            severity="danger"
                            [rounded]="true"
                            [outlined]="true"
                            pTooltip="Delete"
                            (onClick)="openDeleteDialog(realm)"
                        />
                    </div>
                </td>
            </tr>
        </ng-template>
        <ng-template #emptymessage>
            <tr>
                <td colspan="11" class="text-center py-8 text-muted-color">
                    @if (hasActiveFilters()) { No websites match the current
                    filters. } @else { No websites yet. }
                </td>
            </tr>
        </ng-template>
    </p-table>
</div>

<p-dialog
    header="Delete Website"
    [visible]="deleteDialogVisible()"
    (visibleChange)="deleteDialogVisible.set($event)"
    [style]="{ width: '26rem' }"
    [modal]="true"
    [closable]="true"
>
    @if (deleteTarget(); as realm) {
    <div class="flex flex-col gap-4">
        <div class="flex items-center gap-3">
            <i
                class="pi pi-exclamation-triangle text-red-500"
                style="font-size: 2rem"
            ></i>
            <span class="text-surface-700 dark:text-surface-200"
                >This action <strong>cannot be undone</strong>. The website will
                be permanently deleted.</span
            >
        </div>
        <div
            class="border border-surface-200 dark:border-surface-700 rounded p-4 flex flex-col gap-2 bg-surface-50 dark:bg-surface-800"
        >
            <div class="text-lg font-bold">{{ realm.Domain }}</div>
            <div class="text-xs text-muted-color">ID: {{ realm.RealmId }}</div>
        </div>
        @if (deleteError()) {
        <small class="text-red-500">{{ deleteError() }}</small>
        }
    </div>
    }
    <ng-template #footer>
        <div class="flex justify-end gap-2">
            <p-button
                label="Cancel"
                severity="secondary"
                outlined
                [disabled]="deleteLoading()"
                (onClick)="closeDeleteDialog()"
            ></p-button>
            <p-button
                label="Delete"
                icon="pi pi-trash"
                severity="danger"
                [loading]="deleteLoading()"
                (onClick)="executeDelete()"
            ></p-button>
        </div>
    </ng-template>
</p-dialog>
